import { useEffect, useState } from "react";
import api from "@/api";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Badge } from "@/components/ui/badge";
import {
  Building2, Stethoscope, ClipboardList, Star, MessageSquare,
  Hospital, Landmark, ShieldCheck, History, ChevronDown, ChevronRight as ChevronRightIcon,
} from "lucide-react";

/**
 * PERMISSION_GROUPS — the definitive list of receptionist feature flags,
 * organised for the admin permissions modal.  Analytics permissions are
 * deliberately excluded: they are admin-only dashboard tabs.
 *
 * The keys here map 1:1 to `PermissionSet` in
 * `/app/backend/routers/receptionists.py`. When you add a new flag, add it
 * here AND to the backend model.
 */
export const PERMISSION_GROUPS = [
  {
    id: "profile",
    icon: Building2,
    title: "Hospital Profile & Settings",
    permissions: [
      { key: "can_view_public_profile", label: "Public Profile Link & QR Panel" },
      { key: "can_manage_wallet",       label: "UpchaarQ Wallet" },
      { key: "can_view_settlements",    label: "Settlement Statements" },
    ],
  },
  {
    id: "doctors",
    icon: Stethoscope,
    title: "Doctor & Appointment Management",
    permissions: [
      { key: "can_view_doctors",         label: "Doctors List" },
      { key: "can_view_live_diagnosis",  label: "Live Diagnosis Status" },
      { key: "can_view_bookings",        label: "Today's Bookings" },
    ],
  },
  {
    id: "patients",
    icon: ClipboardList,
    title: "Patient & Visit Records",
    permissions: [
      { key: "can_view_reports",         label: "Reports" },
      { key: "can_search_patient_visits", label: "Search Patient Visits" },
      { key: "can_manage_followups",     label: "Follow-ups Management" },
      { key: "can_send_reminders",       label: "Patient Reminders" },
      // Legacy v34 flags — kept so old receptionist dashboards keep working.
      { key: "can_add_patients",         label: "Add Patients (Queue)" },
      { key: "can_view_queue",           label: "View Live Queue" },
      { key: "can_view_appointments",    label: "View Today's Appointments" },
    ],
  },
  {
    id: "feedback",
    icon: Star,
    title: "Feedback & Ratings",
    permissions: [
      { key: "can_view_feedback", label: "Feedback / Patient Ratings" },
    ],
  },
  {
    id: "messaging",
    icon: MessageSquare,
    title: "Messaging & Communication",
    permissions: [
      { key: "can_send_messages", label: "Messaging (WhatsApp Business)" },
    ],
  },
  {
    id: "ipd",
    icon: Hospital,
    title: "IPD Management",
    permissions: [
      {
        key: "can_manage_ipd",
        label: "Manage IPD — Master Access",
        subLabel:
          "Grants access to every IPD sub-feature below. Uncheck to restrict receptionist to individual modules.",
      },
      { key: "can_ipd_wards",       label: "  ↳ Ward Management (create, edit, delete wards)" },
      { key: "can_ipd_beds",        label: "  ↳ Bed Management (allocate, release, maintenance)" },
      { key: "can_ipd_admissions",  label: "  ↳ Patient Admissions (admit, view, transfer)" },
      { key: "can_ipd_notes",       label: "  ↳ Daily Clinical Notes (read / write)" },
      { key: "can_ipd_discharge",   label: "  ↳ Discharge Management (process, print summary)" },
      { key: "can_ipd_billing",     label: "  ↳ Billing (view bills, record payments)" },
      { key: "can_ipd_inventory",   label: "  ↳ Inventory (medicines, supplies, restock)" },
      { key: "can_ipd_reporting",   label: "  ↳ IPD Reporting (view IPD stats & exports)" },
    ],
  },
  {
    id: "schemes",
    icon: Landmark,
    title: "Government Schemes & Verification",
    permissions: [
      { key: "can_manage_gov_schemes",     label: "Gov Schemes Verification (RGHS / Ayushman queue)" },
      { key: "can_manage_rghs_scheme",     label: "RGHS Scheme" },
      { key: "can_manage_ayushman_scheme", label: "Ayushman Bharat" },
      // Legacy alias
      { key: "can_verify_gov_schemes",     label: "Legacy: Verify Gov Scheme cards" },
    ],
  },
];

const ALL_KEYS = PERMISSION_GROUPS.flatMap((g) => g.permissions.map((p) => p.key));
const IPD_SUB_KEYS = [
  "can_ipd_wards", "can_ipd_beds", "can_ipd_admissions", "can_ipd_notes",
  "can_ipd_discharge", "can_ipd_billing", "can_ipd_inventory", "can_ipd_reporting",
];

function countEnabled(perms) {
  return ALL_KEYS.reduce((acc, k) => acc + (perms?.[k] ? 1 : 0), 0);
}

/** Formats a permission-diff entry into a human-readable string. */
function summarisePermDiff(diff) {
  const perms = diff?.permissions;
  if (!perms) return "profile updated";
  const enabled = Object.entries(perms).filter(([, v]) => v?.to === true).map(([k]) => k);
  const disabled = Object.entries(perms).filter(([, v]) => v?.to === false).map(([k]) => k);
  const parts = [];
  if (enabled.length) parts.push(`+${enabled.length} enabled`);
  if (disabled.length) parts.push(`−${disabled.length} disabled`);
  return parts.join("  ·  ") || "no permission change";
}

export default function PermissionsModal({ open, onClose, receptionist, onSaved }) {
  const [perms, setPerms] = useState({});
  const [saving, setSaving] = useState(false);
  const [auditRows, setAuditRows] = useState([]);
  const [auditOpen, setAuditOpen] = useState(false);
  const [auditLoading, setAuditLoading] = useState(false);

  useEffect(() => {
    if (open && receptionist) {
      setPerms(receptionist.permissions || {});
      setAuditOpen(false);
      setAuditRows([]);
    }
  }, [open, receptionist]);

  if (!receptionist) return null;

  const toggle = (key) => {
    setPerms((p) => {
      const next = { ...p, [key]: !p[key] };
      // Master IPD flag cascades to every IPD sub-flag so admins can flip
      // the whole module on or off with one click.
      if (key === "can_manage_ipd") {
        IPD_SUB_KEYS.forEach((sub) => { next[sub] = next[key]; });
      }
      return next;
    });
  };

  const loadAudit = async () => {
    setAuditLoading(true);
    try {
      const { data } = await api.get(`/receptionist/${receptionist.id}/audit-log?limit=10`);
      setAuditRows(data || []);
    } catch (e) {
      toast.error("Failed to load audit log");
    } finally {
      setAuditLoading(false);
    }
  };

  const toggleAudit = async () => {
    const next = !auditOpen;
    setAuditOpen(next);
    if (next && auditRows.length === 0) await loadAudit();
  };

  const setAll = (val) => {
    const next = { ...perms };
    ALL_KEYS.forEach((k) => { next[k] = val; });
    setPerms(next);
  };

  const save = async () => {
    setSaving(true);
    try {
      // Preserve other_features (backend list field) untouched.
      const payload = {
        permissions: {
          ...ALL_KEYS.reduce((acc, k) => ({ ...acc, [k]: !!perms[k] }), {}),
          other_features: receptionist.permissions?.other_features || [],
        },
      };
      await api.put(`/receptionist/${receptionist.id}`, payload);
      toast.success(`Permissions saved for ${receptionist.custom_id}`);
      onSaved?.();
      onClose?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to save permissions");
    } finally {
      setSaving(false);
    }
  };

  const enabled = countEnabled(perms);

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose?.()}>
      <DialogContent className="max-w-2xl p-0 gap-0" data-testid="permissions-modal">
        <DialogHeader className="px-6 pt-6 pb-4 border-b border-slate-200">
          <div className="flex items-start justify-between gap-3">
            <div>
              <DialogTitle className="flex items-center gap-2">
                <ShieldCheck className="w-5 h-5 text-sky-600" />
                Manage Permissions
              </DialogTitle>
              <p className="text-sm text-slate-600 mt-1">
                <span className="font-semibold text-slate-900">{receptionist.full_name}</span>
                {" · "}
                <span className="font-mono text-xs">{receptionist.custom_id}</span>
              </p>
              <p className="text-xs text-slate-500 mt-0.5">
                Select which features this receptionist can access &amp; manage.
              </p>
            </div>
            <Badge variant="outline" className="mt-1 whitespace-nowrap">
              {enabled} / {ALL_KEYS.length} enabled
            </Badge>
          </div>
          <div className="flex gap-2 mt-3">
            <Button variant="outline" size="sm" onClick={() => setAll(true)}
                    data-testid="perms-select-all">Enable all</Button>
            <Button variant="outline" size="sm" onClick={() => setAll(false)}
                    data-testid="perms-clear-all">Clear all</Button>
          </div>
        </DialogHeader>

        <ScrollArea className="max-h-[60vh] px-6 py-4">
          <div className="space-y-6">
            {PERMISSION_GROUPS.map((group) => {
              const Icon = group.icon;
              const groupEnabled = group.permissions.filter((p) => perms[p.key]).length;
              return (
                <section key={group.id} data-testid={`perms-group-${group.id}`}>
                  <div className="flex items-center gap-2 mb-2">
                    <div className="p-1.5 rounded-md bg-sky-50 text-sky-700">
                      <Icon className="w-4 h-4" />
                    </div>
                    <h4 className="font-heading font-semibold text-slate-900 text-sm uppercase tracking-wide">
                      {group.title}
                    </h4>
                    <span className="text-xs text-slate-400">
                      ({groupEnabled}/{group.permissions.length})
                    </span>
                  </div>
                  <div className="space-y-1.5 ml-8">
                    {group.permissions.map((p) => (
                      <label
                        key={p.key}
                        htmlFor={`perm-${p.key}`}
                        className="flex items-start gap-3 cursor-pointer rounded-md px-2 py-1.5 hover:bg-slate-50 transition-colors"
                      >
                        <Checkbox
                          id={`perm-${p.key}`}
                          checked={!!perms[p.key]}
                          onCheckedChange={() => toggle(p.key)}
                          className="mt-0.5"
                          data-testid={`perm-check-${p.key}`}
                        />
                        <div className="flex-1">
                          <p className="text-sm text-slate-800">{p.label}</p>
                          {p.subLabel && (
                            <p className="text-xs text-slate-500 mt-0.5">{p.subLabel}</p>
                          )}
                        </div>
                      </label>
                    ))}
                  </div>
                </section>
              );
            })}

            {/* Audit Log — collapsible, on-demand fetch */}
            <section data-testid="perms-audit-log-section" className="border-t border-slate-200 pt-4">
              <button
                type="button"
                onClick={toggleAudit}
                className="w-full flex items-center gap-2 text-left group"
                data-testid="perms-audit-log-toggle"
              >
                <div className="p-1.5 rounded-md bg-amber-50 text-amber-700">
                  <History className="w-4 h-4" />
                </div>
                <h4 className="font-heading font-semibold text-slate-900 text-sm uppercase tracking-wide">
                  Recent Changes
                </h4>
                <span className="text-xs text-slate-400">who changed what &amp; when</span>
                <span className="ml-auto text-slate-400">
                  {auditOpen ? <ChevronDown className="w-4 h-4" /> : <ChevronRightIcon className="w-4 h-4" />}
                </span>
              </button>

              {auditOpen && (
                <div className="mt-3 ml-8 space-y-2" data-testid="perms-audit-log-body">
                  {auditLoading && (
                    <p className="text-xs text-slate-500">Loading…</p>
                  )}
                  {!auditLoading && auditRows.length === 0 && (
                    <p className="text-xs text-slate-500">
                      No changes recorded yet — the log will start populating once you save.
                    </p>
                  )}
                  {!auditLoading && auditRows.map((r) => (
                    <div
                      key={r.id}
                      className="text-xs border-l-2 border-amber-300 pl-3 py-1"
                      data-testid={`audit-entry-${r.id}`}
                    >
                      <p className="text-slate-800 font-medium">
                        {summarisePermDiff(r.diff)}
                      </p>
                      <p className="text-slate-500 mt-0.5">
                        by <span className="font-mono">{r.changed_by_admin_email || r.changed_by_admin_id}</span>
                        {" · "}
                        {new Date(r.changed_at).toLocaleString("en-IN", {
                          day: "2-digit", month: "short", year: "numeric",
                          hour: "2-digit", minute: "2-digit",
                        })}
                      </p>
                    </div>
                  ))}
                </div>
              )}
            </section>
          </div>
        </ScrollArea>

        <div className="flex gap-2 justify-end px-6 py-4 border-t border-slate-200 bg-slate-50">
          <Button variant="outline" onClick={onClose} disabled={saving} data-testid="perms-cancel">
            Cancel
          </Button>
          <Button onClick={save} disabled={saving}
                  className="bg-sky-500 hover:bg-sky-600 text-white"
                  data-testid="perms-save">
            {saving ? "Saving…" : "Save Permissions"}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
